/**
 * permissionsCatalog.ts
 *
 * The permission register — the list an admin actually grants from.
 *
 * Data only, deliberately: `permissionsCatalog.drift.test.ts` imports this to
 * compare it against corp-dashboard's copy, and a test cannot import a 2000-line
 * client component. Keep React, services and hooks out of this file.
 *
 * The numeric IDs are a fixed contract with the v4 `users_permissions` table.
 * Never renumber them. There is no database table behind them, no uniqueness
 * constraint, and hand-maintained copies in two other apps — which is why the
 * drift test exists.
 *
 * `hidden` marks a permission that is not rendered and, more importantly, is
 * never posted. That distinction is load-bearing: `updateUserPermissions`
 * iterates only the keys it receives, so omitting an ID leaves its grant alone
 * while sending `false` end-dates it. Hiding must mean absent, never false.
 *
 * `module` marks a permission that only means something inside one module, and
 * is offered only for an account with that module switched on (explicitly
 * active — the fail-closed reading the module's own gate uses). It follows the
 * same rule as `hidden` for an account without the module: not rendered and
 * left out of the payload, so switching a module off never strips a grant.
 * Build pickers from `offeredPermissions()` and payloads with
 * `onlyOfferedPermissions()`.
 */

export interface PermissionDef {
  id: number;
  /** Stable slug. Seeded from corp-prospera-auth's `legacyPermissions`. */
  key: string;
  label: string;
  /** One plain sentence saying what the holder can do. Shown to admins. */
  description: string;
  category: PermissionCategory;
  /** Only meaningful alongside the parent; cleared when the parent goes off. */
  children?: PermissionDef[];
  /** Not rendered and never posted. See the note above. */
  hidden?: boolean;
  /**
   * Module key (lowercased title, e.g. 'surgery'). Offered only where that
   * module is explicitly active; must match corp-dashboard's copy.
   */
  module?: string;
}

export type PermissionCategory =
  | 'Q/A'
  | 'Patient portals'
  | 'Scheduling'
  | 'Communication'
  | 'Prescribing'
  | 'Documents & Drive'
  | 'Marketing'
  | 'Payments'
  | 'Deleting & merging'
  | 'Reporting & tools'
  | 'Administration';

/**
 * Fixed display order. Categories render in this order; permissions sort
 * alphabetically *within* a category. Hand-set rather than alphabetical so the
 * everyday grants come first and the destructive ones sit together.
 */
export const CATEGORY_ORDER: PermissionCategory[] = [
  'Q/A',
  'Patient portals',
  'Scheduling',
  'Communication',
  'Prescribing',
  'Documents & Drive',
  'Marketing',
  'Payments',
  'Deleting & merging',
  'Reporting & tools',
  'Administration',
];

export const PERMISSIONS: PermissionDef[] = [
  {
    id: 1,
    key: 'qa_visibility',
    label: 'Q/A Visibility',
    description: "Can see Quality Assurance cases, both the main list and a patient's Q/A tab.",
    category: 'Q/A',
  },
  {
    id: 24,
    key: 'qa_submission',
    label: 'Q/A Submission',
    description: 'Can open a new Quality Assurance case.',
    category: 'Q/A',
  },
  {
    id: 25,
    key: 'qa_status',
    label: 'Q/A Status',
    description: "Can change a Quality Assurance case's status.",
    category: 'Q/A',
  },

  // NOT hidden: the Consults module gates on this permission, so it has to be
  // grantable from the User Permissions modal or no V5 admin can open the gate.
  // It was already grantable in corp-dashboard. `hidden` is allowed to differ
  // between the two apps by design, so this does not put the catalogs into
  // drift. Consult Portal is offered only where the Consults module is on:
  // without it V5 lets nobody into Consults, so the grant would mean nothing.
  {
    id: 5,
    key: 'consult_portal',
    label: 'Consult Portal',
    description: 'Can open the Consults list.',
    category: 'Patient portals',
    module: 'consults',
  },
  // NOT hidden: the Surgery module gates on this permission, so it has to be
  // grantable from the User Permissions modal or no V5 admin can open the gate.
  // It was already grantable in corp-dashboard. `hidden` is allowed to differ
  // between the two apps by design, so this does not put the catalogs into
  // drift. Surgeries is offered only where the Surgery module is on: without
  // it V5 lets nobody into Surgery, so the grant would mean nothing there.
  {
    id: 6,
    key: 'surgery_portal',
    label: 'Surgeries',
    description: 'Can open the Surgery list.',
    category: 'Patient portals',
    module: 'surgery',
  },
  // NOT hidden: the Post-ops module gates on this permission, so it has to be
  // grantable from the User Permissions modal or no V5 admin can open the gate.
  // It was already grantable in corp-dashboard, which is why the gap was easy
  // to miss. `hidden` is allowed to differ between the two apps by design, so
  // this does not put the catalogs into drift.
  {
    id: 7,
    key: 'post_op_portal',
    label: 'Post Op Portal',
    description: 'Can open the Post Op list.',
    category: 'Patient portals',
  },
  {
    id: 12,
    key: 'inquiry_portal',
    label: 'Inquiry Portal',
    description: 'Can open the Inquiries list.',
    category: 'Patient portals',
  },
  // Level 4 and above can always do this; the grant is for level 3 and below.
  {
    id: 35,
    key: 'manage_store_availability',
    label: 'Manage Store Availability',
    description:
      'Can add products to the store or remove them. Account Admins can always do this.',
    category: 'Patient portals',
  },

  {
    id: 9,
    key: 'manage_time_blocks',
    label: 'Manage Time Blocks',
    description: 'Can create, edit and remove blocked-off time on the calendar.',
    category: 'Scheduling',
  },
  {
    id: 22,
    key: 'scheduler_preset_sharing',
    label: 'Scheduler Preset Sharing',
    description:
      'Can share a saved calendar view with everyone on the account. This is enforced in the app only.',
    category: 'Scheduling',
  },

  {
    id: 3,
    key: 'messages',
    label: 'Messages',
    description: 'Can see the Messages section of the Message Center.',
    category: 'Communication',
  },
  {
    id: 8,
    key: 'call_audio',
    label: 'Call Audio',
    description: "Can download the recording of a phone call from a patient's communication log.",
    category: 'Communication',
  },

  {
    id: 4,
    key: 'eprescribe',
    label: 'E-prescribe',
    description: "Can open the Prescriptions tab on a patient's chart.",
    category: 'Prescribing',
  },

  {
    id: 2,
    key: 'google_drive_rw',
    label: 'Google Drive Read/Write',
    description: "Can open the Google Drive tab on a patient's chart.",
    category: 'Documents & Drive',
  },
  {
    id: 13,
    key: 'google_drive_delete',
    label: 'Google Drive Delete',
    description: "Can delete files from a patient's Google Drive folder.",
    category: 'Documents & Drive',
  },

  {
    id: 20,
    key: 'marketing_access',
    label: 'Marketing Access',
    description: 'Can open the Marketing section and send bulk email or SMS from any list.',
    category: 'Marketing',
    children: [
      {
        id: 21,
        key: 'email_editor_full',
        label: 'Email Editor Full Access',
        description: 'Can edit email templates freely instead of viewing them locked.',
        category: 'Marketing',
      },
      {
        id: 27,
        key: 'marketing_campaigns',
        label: 'Marketing Campaigns',
        description: 'Can build and send email and SMS campaigns.',
        category: 'Marketing',
      },
      {
        id: 28,
        key: 'marketing_automations',
        label: 'Marketing Automations',
        description: 'Can build and run email and SMS automations.',
        category: 'Marketing',
      },
      {
        id: 29,
        key: 'marketing_push',
        label: 'Marketing Push Notifications',
        description: 'Can build and send push notifications.',
        category: 'Marketing',
      },
      {
        id: 30,
        key: 'marketing_app_posts',
        label: 'Marketing App Posts',
        description: 'Can manage in-app posts and see app activity.',
        category: 'Marketing',
      },
      {
        id: 31,
        key: 'marketing_settings',
        label: 'Marketing Settings',
        description: 'Can change marketing senders, from-numbers and compliance settings.',
        category: 'Marketing',
      },
      {
        id: 32,
        key: 'apple_tv_signage',
        label: 'Apple TV Signage',
        description: 'Can change what plays on the waiting-room screens.',
        category: 'Marketing',
      },
    ],
  },

  {
    id: 14,
    key: 'gift_credit',
    label: 'Gift Card',
    description:
      'Can issue, edit and comp gift credits, and change the balance on a gift credit payment.',
    category: 'Payments',
  },
  // Two grants, not one. Reconciling deposits and answering chargebacks are
  // different jobs, and a bookkeeper usually needs the first without the second.
  {
    id: 33,
    key: 'payouts',
    label: 'Payouts',
    description:
      "Can see deposits landing in the practice's bank account, and the payments inside each one.",
    category: 'Payments',
  },
  {
    id: 34,
    key: 'disputes',
    label: 'Disputes',
    description: "Can see and respond to card chargebacks raised against the practice's payments.",
    category: 'Payments',
  },

  {
    id: 10,
    key: 'delete_plan_notes',
    label: 'Delete Plan Notes',
    description: "Can delete internal office notes from a patient's communication log.",
    category: 'Deleting & merging',
  },
  // Hidden because it controls nothing. No reader exists in this app, the V5
  // API, or the legacy tree; appointment deletion is gated on the user's access
  // level instead (AppointmentExtrasController::hardDelete). Left in the
  // catalog so existing grants are untouched and the drift test still sees it.
  {
    id: 16,
    key: 'delete_appointments',
    label: 'Delete Appointments',
    description:
      "Appointment deletion is controlled by the user's access level, not by this setting.",
    category: 'Deleting & merging',
    hidden: true,
  },
  {
    id: 17,
    key: 'delete_tasks',
    label: 'Delete Tasks',
    description: "Can deactivate a task from a patient's task panel.",
    category: 'Deleting & merging',
  },
  {
    id: 18,
    key: 'merge_patients',
    label: 'Merge Patients',
    description:
      'Can merge two patient records in the older system. Merging from the new patient chart is not controlled by this setting.',
    category: 'Deleting & merging',
  },
  {
    id: 19,
    key: 'delete_products',
    label: 'Delete Products',
    description:
      "Can delete a product from the catalogue, and remove a product from a patient's stock.",
    category: 'Deleting & merging',
  },
  {
    id: 23,
    key: 'delete_labs',
    label: 'Delete Labs',
    description: 'Can delete a lab document, and see ones already deleted.',
    category: 'Deleting & merging',
  },

  {
    id: 11,
    key: 'ui_2_0_beta',
    label: '2.0 UI/UX (BETA)',
    description: 'Can use the updated look for the older system’s pages.',
    category: 'Reporting & tools',
  },
  {
    id: 15,
    key: 'reports',
    label: 'Reports',
    description:
      'Can open Standard Reports, the report builder, presets and scheduled reports. Reports someone owns or that were shared with them stay available without it.',
    category: 'Reporting & tools',
  },
  {
    id: 26,
    key: 'ai_assistant',
    label: 'AI Assistant',
    description: 'Can use the AI assistant.',
    category: 'Reporting & tools',
  },

  // Replaces the user-level gate on the whole Admin area. Level 5 always passes
  // without it; nobody else does, whatever their level.
  {
    id: 36,
    key: 'admin_access',
    label: 'Admin Access',
    description: 'Can open and change every Admin section for this account.',
    category: 'Administration',
  },
  // Off for everyone at launch and never granted to new users. Only works for
  // level 3 and above; only level 4 and above can grant it.
  {
    id: 37,
    key: 'user_audit',
    label: 'User Audit',
    description:
      'Can see what each staff member opened, changed and exported, and download it.',
    category: 'Reporting & tools',
  },
];

/**
 * The tree flattened depth-first, parents followed by their children —
 * including hidden entries. The nesting is a picker affordance only; everywhere
 * that treats every permission equally reads this rather than walking
 * `PERMISSIONS` and silently dropping children.
 *
 * Exported for `UsersAdmin.permissions.test.ts`, which asserts no duplicate ID
 * and no duplicate label across the whole register.
 */
export const FLAT_PERMISSIONS: PermissionDef[] = PERMISSIONS.flatMap((p) => [
  p,
  ...(p.children ?? []),
]);

/**
 * Every ID the modal loads and saves. Hidden entries are excluded on purpose:
 * posting one as `false` would end-date a grant that corp-dashboard still hands
 * out. Omission is safe; `false` is destructive.
 */
export const ALL_PERMISSION_IDS: number[] = FLAT_PERMISSIONS.filter((p) => !p.hidden).map(
  (p) => p.id,
);

/** IDs this app deliberately does not show. The drift test reads this. */
export const HIDDEN_PERMISSION_IDS: number[] = FLAT_PERMISSIONS.filter((p) => p.hidden).map(
  (p) => p.id,
);

export interface PermissionCategoryGroup {
  category: PermissionCategory;
  items: PermissionDef[];
}

/**
 * Top-level permissions grouped into `CATEGORY_ORDER`, alphabetical within each
 * category, hidden entries dropped. Children stay attached to their parent and
 * are sorted there.
 */
export function groupByCategory(defs: PermissionDef[] = PERMISSIONS): PermissionCategoryGroup[] {
  const byLabel = (a: PermissionDef, b: PermissionDef) => a.label.localeCompare(b.label);

  return CATEGORY_ORDER.map((category) => ({
    category,
    items: defs
      .filter((p) => p.category === category && !p.hidden)
      .map((p) => (p.children ? { ...p, children: [...p.children].sort(byLabel) } : p))
      .sort(byLabel),
  })).filter((group) => group.items.length > 0);
}

/**
 * Filters grouped permissions by a free-text query, dropping empty categories.
 * Mirrors the shape of `filterSections` in `v5/components/RecipientPicker.tsx`
 * without importing it — that function's section and item types are closed
 * unions for message recipients and will not accept permissions.
 *
 * A parent that matches keeps *all* of its children, matching or not. Without
 * that, filtering to a matched parent whose children are hidden and toggling it
 * off would clear grants the admin never saw.
 *
 * A child that matches while its parent does not pulls the parent in as
 * context, carrying only the matching children.
 */
export function filterCategories(
  groups: PermissionCategoryGroup[],
  query: string,
): PermissionCategoryGroup[] {
  const q = query.trim().toLowerCase();
  if (!q) return groups;

  const matches = (p: PermissionDef) =>
    p.label.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);

  return groups
    .map((group) => ({
      category: group.category,
      items: group.items.flatMap((p) => {
        if (matches(p)) return [p];
        const hits = (p.children ?? []).filter(matches);
        return hits.length > 0 ? [{ ...p, children: hits }] : [];
      }),
    }))
    .filter((group) => group.items.length > 0);
}

const isOffered = (p: PermissionDef, activeModules: readonly string[]): boolean =>
  !p.module || activeModules.includes(p.module);

/**
 * The register as offered to one account: entries whose `module` is not among
 * `activeModules` (the account's explicitly active module keys) are dropped,
 * children included. Unknown modules — still loading, failed — means none, so a
 * module's permission stays out until the account is known to have it.
 */
export function offeredPermissions(
  activeModules: readonly string[],
  defs: PermissionDef[] = PERMISSIONS,
): PermissionDef[] {
  return defs
    .filter((p) => isOffered(p, activeModules))
    .map((p) =>
      p.children ? { ...p, children: p.children.filter((c) => isOffered(c, activeModules)) } : p,
    );
}

/** Every ID a payload may carry for an account with these modules on. */
export function offeredPermissionIds(activeModules: readonly string[]): number[] {
  const offered = new Set(
    offeredPermissions(activeModules)
      .flatMap((p) => [p, ...(p.children ?? [])])
      .map((p) => p.id),
  );
  return ALL_PERMISSION_IDS.filter((id) => offered.has(id));
}

/**
 * A selection map with every ID the account is not offered taken out — not set
 * false, taken out — so posting it leaves those grants exactly as they are.
 */
export function onlyOfferedPermissions(
  selected: Record<number, boolean>,
  activeModules: readonly string[],
): Record<number, boolean> {
  const offered = new Set(offeredPermissionIds(activeModules));
  const out: Record<number, boolean> = {};
  for (const [id, value] of Object.entries(selected)) {
    if (offered.has(Number(id))) out[Number(id)] = value;
  }
  return out;
}

/**
 * Every grantable permission in one alphabetical run, children still nested
 * under their parent and alphabetical among themselves.
 *
 * This is `groupByCategory`'s sort applied across the whole register rather
 * than within each category. The picker opens on this, so an admin who knows
 * what a permission is called does not have to work out which category someone
 * filed it under first.
 */
export function sortedPermissions(defs: PermissionDef[] = PERMISSIONS): PermissionDef[] {
  const byLabel = (a: PermissionDef, b: PermissionDef) => a.label.localeCompare(b.label);

  return defs
    .filter((p) => !p.hidden)
    .map((p) =>
      p.children
        ? { ...p, children: [...p.children].filter((c) => !c.hidden).sort(byLabel) }
        : p,
    )
    .sort(byLabel);
}

export interface PermissionFilter {
  query?: string;
  category?: PermissionCategory | null;
}

/**
 * Narrows a flat list by a search term, a category, or both.
 *
 * The parent/child rules are `filterCategories`', for the same reasons: a
 * parent that matches keeps *all* of its children, so toggling it off cannot
 * clear a grant the admin never saw; a child that matches while its parent does
 * not pulls the parent in as context, carrying only the matching children.
 *
 * The category applies to the top-level entry — a child travels with its
 * parent rather than being judged on its own.
 */
export function filterPermissions(
  defs: PermissionDef[],
  { query = '', category = null }: PermissionFilter = {},
): PermissionDef[] {
  const q = query.trim().toLowerCase();

  const matches = (p: PermissionDef) =>
    p.label.toLowerCase().includes(q) || p.description.toLowerCase().includes(q);

  const searched = !q
    ? defs
    : defs.flatMap((p) => {
        if (matches(p)) return [p];
        const hits = (p.children ?? []).filter(matches);
        return hits.length > 0 ? [{ ...p, children: hits }] : [];
      });

  return category ? searched.filter((p) => p.category === category) : searched;
}

export type PermissionCounts = Record<PermissionCategory | 'all', number>;

/**
 * How many rows each category would show for the list it is given.
 *
 * Pass the SEARCH-filtered list, not the whole register: the nav counts are
 * meant to describe what is actually there to click, so a category the search
 * has emptied reads zero and the nav can stop responding to it.
 */
export function categoryCounts(defs: PermissionDef[]): PermissionCounts {
  const counts = { all: 0 } as PermissionCounts;
  for (const category of CATEGORY_ORDER) counts[category] = 0;

  for (const p of defs) {
    const rows = 1 + (p.children?.length ?? 0);
    counts.all += rows;
    counts[p.category] += rows;
  }

  return counts;
}
